import { useMemo } from 'react'
import { CsField, CsInfoRow, CsSection } from '@/components/Cs/CsSection'
import { csFieldClass } from '@/components/Cs/csFormStyles'
import { useCsTeamOptions } from '@/hooks/useCsTeamOptions'
import { CS_STAGE_LABELS, type CsAccountDetail, type CsCarteiraStage } from '@/services/csService'

type Props = {
  account: CsAccountDetail
  onSave: (payload: Record<string, unknown>) => Promise<void>
}

export function CsAccountDados({ account, onSave }: Props) {
  const { options: csOptions, loading: csLoading } = useCsTeamOptions(true)

  const csSelectOptions = useMemo(() => {
    const rows = [...csOptions]
    if (account.csm && !rows.some((row) => row.id === account.csm!.id)) {
      rows.unshift({ id: account.csm.id, name: account.csm.name })
    }
    return rows
  }, [account.csm, csOptions])

  return (
    <CsSection title='Dados do cliente'>
      <CsField label='CS que atende (suporte)'>
        <select
          className={csFieldClass}
          value={account.csm?.id ?? ''}
          disabled={csLoading}
          onChange={(e) => void onSave({ csmUserId: e.target.value || null })}
        >
          <option value=''>{csLoading ? 'Carregando…' : 'Sem CS definido'}</option>
          {csSelectOptions.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </CsField>

      <CsField label='Status'>
        <input
          className={csFieldClass}
          defaultValue={account.status}
          onBlur={(e) => {
            if (e.target.value !== account.status) void onSave({ status: e.target.value })
          }}
        />
      </CsField>
      <CsField label='Estágio CS'>
        <select
          className={csFieldClass}
          value={account.csStage}
          onChange={(e) => void onSave({ csStage: e.target.value as CsCarteiraStage })}
        >
          {(Object.keys(CS_STAGE_LABELS) as CsCarteiraStage[]).map((s) => (
            <option key={s} value={s}>
              {CS_STAGE_LABELS[s]}
            </option>
          ))}
        </select>
      </CsField>
      <CsField label='Classificação'>
        <select
          className={csFieldClass}
          value={account.classificacao ?? ''}
          onChange={(e) => void onSave({ classificacao: e.target.value || null })}
        >
          <option value=''>Sem classificação</option>
          <option value='PRATA'>Prata</option>
          <option value='OURO'>Ouro</option>
          <option value='DIAMANTE'>Diamante</option>
        </select>
      </CsField>
      <CsField label='Plano contratado'>
        <input
          className={csFieldClass}
          defaultValue={account.planoContratado ?? account.deal.servico ?? ''}
          onBlur={(e) => void onSave({ planoContratado: e.target.value || null })}
        />
      </CsField>
      <CsField label='Focal'>
        <input
          className={csFieldClass}
          defaultValue={account.focalNome ?? ''}
          onBlur={(e) => void onSave({ focalNome: e.target.value || null })}
        />
      </CsField>
      <CsField label='Squad'>
        <input
          className={csFieldClass}
          defaultValue={account.squadNome ?? ''}
          onBlur={(e) => void onSave({ squadNome: e.target.value || null })}
        />
      </CsField>
      <CsInfoRow label='Data de entrada' value={new Date(account.dataInicio).toLocaleDateString('pt-BR')} />
      <CsInfoRow label='Head' value={account.head?.name} />
      <CsInfoRow label='Responsável interno' value={account.responsavelInterno?.name} />
      <CsInfoRow label='Closer' value={account.closer.name} />
      <CsInfoRow label='CNPJ' value={account.empresa.cnpj} />
      {account.finance && (
        <>
          <CsInfoRow label='MRR' value={formatMoney(account.finance.mrr)} />
          <CsInfoRow label='Receita / LTV' value={formatMoney(account.finance.receita)} />
          <CsInfoRow label='Potencial expansão' value={formatMoney(account.finance.potencialExpansao)} />
          <CsInfoRow label='Impacto churn' value={formatMoney(account.finance.impactoChurn)} />
        </>
      )}
    </CsSection>
  )
}

function formatMoney(value: number | null | undefined) {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
